<script lang="ts">
	import { marked } from 'marked';
	import DOMPurify from 'isomorphic-dompurify';
	import { slide } from 'svelte/transition';

	interface Props {
		value?: string;
		placeholder?: string;
		label?: string;
		helpText?: string;
		rows?: number;
		disabled?: boolean;
		required?: boolean;
		class?: string;
		id?: string;
		marketId?: number;
		restrictedMode?: boolean;
		hideImageButton?: boolean;
		onchange?: (value: string) => void;
	}

	let {
		value = $bindable(''),
		placeholder = '',
		label = '',
		helpText = '',
		rows = 10,
		disabled = false,
		required = false,
		class: className = '',
		id,
		marketId,
		restrictedMode = false,
		hideImageButton = false,
		onchange,
	}: Props = $props();

	let showPreview = $state(false);
	let previewHtml = $state('');
	let textareaElement = $state<HTMLTextAreaElement>();
	let fileInput = $state<HTMLInputElement>();
	let uploading = $state(false);
	let uploadError = $state<string | null>(null);

	// Use external id if provided, otherwise generate unique ID for textarea
	const textareaId = id || `markdown-textarea-${Math.random().toString(36).substring(2, 11)}`;

	// Update preview when value changes or preview is toggled
	$effect(() => {
		if (showPreview) {
			updatePreview();
		}
	});

	async function updatePreview() {
		try {
			const html = await marked(value || '');
			if (restrictedMode) {
				// Use same restrictions as MarkdownRenderer in restricted mode
				previewHtml = DOMPurify.sanitize(html, {
					ALLOWED_TAGS: [
						'p',
						'br',
						'strong',
						'b',
						'em',
						'i',
						'u',
						'ul',
						'ol',
						'li',
						'h3',
						'h4',
						'h5',
						'h6',
						'blockquote',
						'del',
						'ins',
						'code',
						'pre',
					],
					ALLOWED_ATTR: [],
					KEEP_CONTENT: true,
				});
			} else {
				// For email templates with inline styles, use ADD_ATTR to ensure
				// style attributes and table attributes pass through DOMPurify
				previewHtml = DOMPurify.sanitize(html, {
					ADD_TAGS: ['#comment', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td'], // Allow HTML comments and table tags
					ADD_ATTR: [
						'style',
						'cellpadding',
						'cellspacing',
						'border',
						'align',
						'valign',
						'width',
						'height',
						'colspan',
						'rowspan',
					],
					KEEP_CONTENT: true,
					WHOLE_DOCUMENT: false,
					RETURN_DOM: false,
					RETURN_DOM_FRAGMENT: false,
					SAFE_FOR_TEMPLATES: false,
				});
			}
		} catch (error) {
			console.error('Error rendering markdown:', error);
			previewHtml = '<p class="text-red-600">Error rendering preview</p>';
		}
	}

	function insertText(before: string, after: string = '', defaultText: string = '') {
		if (!textareaElement) return;

		const start = textareaElement.selectionStart;
		const end = textareaElement.selectionEnd;
		const selectedText = value.substring(start, end) || defaultText;

		const newValue =
			value.substring(0, start) + before + selectedText + after + value.substring(end);

		value = newValue;

		// Set cursor position after insertion
		setTimeout(() => {
			if (textareaElement) {
				const newPosition = start + before.length + selectedText.length;
				textareaElement.setSelectionRange(newPosition, newPosition);
				textareaElement.focus();
			}
		}, 0);

		if (onchange) onchange(value);
	}

	function handleBold() {
		insertText('**', '**', 'bold text');
	}

	function handleItalic() {
		insertText('*', '*', 'italic text');
	}

	function handleLink() {
		insertText('[', '](url)', 'link text');
	}

	function handleList() {
		if (!textareaElement) return;

		const start = textareaElement.selectionStart;
		const lineStart = value.lastIndexOf('\n', start - 1) + 1;
		const before = value.substring(0, lineStart);
		const after = value.substring(lineStart);

		value = before + '- ' + after;

		setTimeout(() => {
			if (textareaElement) {
				textareaElement.setSelectionRange(lineStart + 2, lineStart + 2);
				textareaElement.focus();
			}
		}, 0);

		if (onchange) onchange(value);
	}

	function handleNumberedList() {
		if (!textareaElement) return;

		const start = textareaElement.selectionStart;
		const lineStart = value.lastIndexOf('\n', start - 1) + 1;
		const before = value.substring(0, lineStart);
		const after = value.substring(lineStart);

		value = before + '1. ' + after;

		setTimeout(() => {
			if (textareaElement) {
				textareaElement.setSelectionRange(lineStart + 3, lineStart + 3);
				textareaElement.focus();
			}
		}, 0);

		if (onchange) onchange(value);
	}

	function handleHeading() {
		if (!textareaElement) return;

		const start = textareaElement.selectionStart;
		const lineStart = value.lastIndexOf('\n', start - 1) + 1;
		const before = value.substring(0, lineStart);
		const after = value.substring(lineStart);

		value = before + '## ' + after;

		setTimeout(() => {
			if (textareaElement) {
				textareaElement.setSelectionRange(lineStart + 3, lineStart + 3);
				textareaElement.focus();
			}
		}, 0);

		if (onchange) onchange(value);
	}

	async function handleImageUpload() {
		if (!fileInput) return;
		fileInput.click();
	}

	async function uploadImage(file: File) {
		if (!file.type.startsWith('image/')) {
			uploadError = 'Please select an image file';
			return;
		}

		uploading = true;
		uploadError = null;

		const formData = new FormData();
		formData.append('document', file);
		formData.append('name', file.name);
		if (marketId) {
			formData.append('marketId', marketId.toString());
		}

		try {
			const response = await fetch('/api/documents/upload', {
				method: 'POST',
				body: formData,
			});

			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.message || 'Upload failed');
			}

			const result = await response.json();

			// Check if result has the expected structure
			if (!result.success || !result.data?.url) {
				throw new Error('Invalid response format from server');
			}

			// Insert markdown image syntax at cursor position
			const imageMarkdown = `![${file.name}](${result.data.url})`;
			insertText(imageMarkdown, '', '');
		} catch (error) {
			console.error('Image upload error:', error);
			uploadError = error instanceof Error ? error.message : 'Failed to upload image';
		} finally {
			uploading = false;
		}
	}

	function handleFileSelect(event: Event) {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (file) {
			uploadImage(file);
		}
		// Clear the input so the same file can be selected again
		input.value = '';
	}

	function togglePreview() {
		showPreview = !showPreview;
		// The $effect will handle updating the preview
	}

	function handleInput() {
		if (onchange) onchange(value);
	}
</script>

<div class="markdown-editor {className}">
	{#if label}
		<label for={textareaId} class="form-label">
			{label}
			{#if required}
				<span class="text-red-500">*</span>
			{/if}
		</label>
	{/if}

	{#if helpText}
		<p class="help-text">{helpText}</p>
	{/if}

	<div class="editor-container">
		<div class="toolbar">
			<div class="toolbar-group">
				<button
					type="button"
					class="toolbar-btn"
					onclick={handleBold}
					title="Bold (Ctrl+B)"
					{disabled}
				>
					<strong>B</strong>
				</button>
				<button
					type="button"
					class="toolbar-btn"
					onclick={handleItalic}
					title="Italic (Ctrl+I)"
					{disabled}
				>
					<em>I</em>
				</button>
				<button
					type="button"
					class="toolbar-btn"
					onclick={handleHeading}
					title="Heading"
					{disabled}
				>
					H
				</button>
			</div>

			{#if !restrictedMode}
				<div class="toolbar-group">
					<button
						type="button"
						class="toolbar-btn"
						onclick={handleLink}
						title="Insert Link"
						{disabled}
					>
						🔗
					</button>
					{#if !hideImageButton}
						<button
							type="button"
							class="toolbar-btn"
							onclick={handleImageUpload}
							title="Upload Image"
							disabled={disabled || uploading}
						>
							{uploading ? '⏳' : '🖼️'}
						</button>
					{/if}
				</div>
			{/if}

			<div class="toolbar-group">
				<button
					type="button"
					class="toolbar-btn"
					onclick={handleList}
					title="Bullet List"
					{disabled}
				>
					• —
				</button>
				<button
					type="button"
					class="toolbar-btn"
					onclick={handleNumberedList}
					title="Numbered List"
					{disabled}
				>
					1. —
				</button>
			</div>

			<div class="toolbar-group ml-auto">
				<a
					href="https://www.markdownguide.org/basic-syntax/"
					target="_blank"
					rel="noopener noreferrer"
					class="markdown-help-link"
					title="Markdown formatting help"
				>
					Markdown Help
				</a>
				<button
					type="button"
					class="toolbar-btn preview-btn"
					class:active={showPreview}
					onclick={togglePreview}
				>
					{showPreview ? 'Edit' : 'Preview'}
				</button>
			</div>
		</div>

		{#if uploadError}
			<div class="upload-error" transition:slide={{ duration: 200 }}>
				{uploadError}
				<button type="button" class="close-error" onclick={() => (uploadError = null)}> × </button>
			</div>
		{/if}

		<div class="editor-content">
			{#if !showPreview}
				<textarea
					id={textareaId}
					bind:this={textareaElement}
					bind:value
					oninput={handleInput}
					{placeholder}
					{rows}
					{disabled}
					{required}
					class="editor-textarea"
				></textarea>
			{:else}
				<div class="preview-pane">
					{#if value}
						{@html previewHtml}
					{:else}
						<p class="preview-placeholder">Nothing to preview</p>
					{/if}
				</div>
			{/if}
		</div>
	</div>

	<input
		bind:this={fileInput}
		type="file"
		accept="image/*"
		onchange={handleFileSelect}
		class="hidden"
	/>
</div>

<style>
	.markdown-editor {
		width: 100%;
	}

	.form-label {
		display: block;
		margin-bottom: var(--lg-space-2);
		font-weight: var(--lg-font-medium);
		color: var(--lg-text-primary);
	}

	.help-text {
		margin-top: calc(-1 * var(--lg-space-1));
		margin-bottom: var(--lg-space-2);
		font-size: var(--lg-text-sm);
		color: var(--lg-text-secondary);
	}

	.editor-container {
		border: 1px solid var(--lg-border-light);
		border-radius: var(--lg-radius-sm);
		overflow: hidden;
		background: var(--lg-bg-primary);
	}

	.toolbar {
		display: flex;
		gap: var(--lg-space-2);
		padding: var(--lg-space-2);
		background: var(--lg-bg-secondary);
		border-bottom: 1px solid var(--lg-border-light);
		flex-wrap: wrap;
	}

	.toolbar-group {
		display: flex;
		gap: var(--lg-space-1);
	}

	.toolbar-btn {
		padding: var(--lg-space-1) var(--lg-space-2);
		font-size: var(--lg-text-sm);
		border: 1px solid var(--lg-border-light);
		background: var(--lg-bg-primary);
		border-radius: var(--lg-radius-sm);
		cursor: pointer;
		transition: var(--lg-transition-fast);
		min-width: var(--lg-space-8);
		height: var(--lg-space-8);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.toolbar-btn:hover:not(:disabled) {
		background: var(--lg-bg-hover);
		border-color: var(--lg-border-primary);
	}

	.toolbar-btn:active:not(:disabled) {
		background: var(--lg-bg-tertiary);
	}

	.toolbar-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.preview-btn {
		padding: var(--lg-space-1) var(--lg-space-3);
		width: auto;
	}

	.preview-btn.active {
		background: var(--lg-primary);
		color: var(--lg-text-white);
		border-color: var(--lg-primary);
	}

	.ml-auto {
		margin-left: auto;
	}

	.markdown-help-link {
		display: flex;
		align-items: center;
		padding: 0.25rem 0.75rem;
		font-size: 0.875rem;
		color: var(--lg-primary);
		text-decoration: none;
		border-radius: 0.25rem;
		transition: all 0.15s;
	}

	.markdown-help-link:hover,
	.markdown-help-link:focus,
	.markdown-help-link:active {
		background: var(--lg-bg-hover);
		text-decoration: underline;
	}

	.upload-error {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.5rem 0.75rem;
		background: var(--lg-bg-error);
		color: var(--lg-error);
		font-size: 0.875rem;
		border-bottom: 1px solid var(--lg-error-light);
	}

	.close-error {
		padding: 0;
		background: none;
		border: none;
		color: var(--lg-error);
		font-size: 1.25rem;
		cursor: pointer;
		line-height: 1;
	}

	.editor-content {
		position: relative;
		min-height: 10rem;
	}

	.editor-textarea {
		width: 100%;
		padding: 0.75rem;
		border: none;
		resize: vertical;
		font-family: inherit;
		font-size: inherit;
		line-height: 1.5;
		outline: none;
		background: var(--lg-bg-primary);
	}

	.preview-pane {
		padding: 0.75rem;
		min-height: 10rem;
		background: var(--lg-bg-primary);
		overflow: auto;
	}

	.preview-placeholder {
		color: var(--lg-text-light);
		font-style: italic;
	}

	.hidden {
		display: none;
	}

	/* Mobile responsive */
	@media (max-width: 640px) {
		.toolbar {
			gap: 0.25rem;
			padding: 0.375rem;
		}

		.toolbar-btn {
			font-size: 0.75rem;
			padding: 0.25rem 0.375rem;
			min-width: 1.75rem;
			height: 1.75rem;
		}
	}
</style>
